import { Component, inject, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { BasicFolder, isAsset, isFolder } from '@r1-compass-library/models';
import { BasicFolderSearch } from '@r1-compass-library/models/search/basic-folder-search.model';
import { Observable, of, switchMap, tap } from 'rxjs';
import { FileTableItem } from '../../../../public-api';
import { FolderRestService } from '@r1-compass-library/services/rest/folder-rest.service';

export interface MoveFileDialogData {
    iamDivisionId: number;
    item: FileTableItem;
    folderService: FolderRestService<BasicFolder, BasicFolderSearch>;
}

@Component({
    selector: 'r1-compass-lib-move-file',
    imports: [MatButtonModule, MatDialogModule, MatIconModule, MatTableModule],
    templateUrl: './move-file.component.html',
    styleUrl: './move-file.component.scss',
})
export class MoveFileComponent implements OnInit {
    dialogRef = inject<MatDialogRef<MoveFileComponent, number | null>>(MatDialogRef);
    data = inject<MoveFileDialogData>(MAT_DIALOG_DATA);

    folders: BasicFolder[] = [];
    originFolderId: number | null | undefined;
    currentFolder: BasicFolder | undefined;

    displayedColumns = ['name'];
    path = ['root'];

    ngOnInit() {
        if (isFolder(this.data.item)) {
            this.originFolderId = this.data.item.parentFolderId;
        } else if (isAsset(this.data.item)) {
            this.originFolderId = this.data.item.folderId;
        }
        this.loadFolderContents().subscribe();
    }

    navigateBack() {
        let folderSource: Observable<BasicFolder | undefined>;
        if (this.currentFolder?.parentFolderId) {
            folderSource = this.data.folderService.get(this.currentFolder.parentFolderId);
        } else {
            folderSource = of(undefined);
        }
        folderSource
            .pipe(
                switchMap(folder => this.loadFolderContents(folder)),
                tap(() => this.path.pop())
            )
            .subscribe();
    }

    navigateIntoFolder(folder: BasicFolder) {
        this.loadFolderContents(folder)
            .pipe(tap(() => this.path.push(folder.name)))
            .subscribe();
    }

    loadFolderContents(folder?: BasicFolder): Observable<BasicFolder[]> {
        const search: BasicFolderSearch = { iamDivisionId: this.data.iamDivisionId };
        if (!folder?.id) {
            search.rootFolder = true;
        } else {
            search.parentFolderId = folder.id;
        }
        return this.data.folderService.search(search).pipe(
            tap(folders => {
                this.currentFolder = folder;
                this.folders = folders.filter(folder => {
                    if (isFolder(this.data.item)) {
                        return folder.id !== this.data.item.id;
                    }
                    return folder;
                });
            })
        );
    }

    move() {
        const defaultFolderId = isFolder(this.data.item) ? 0 : isAsset(this.data.item) ? null : 0;
        this.dialogRef.close(this.currentFolder?.id ?? defaultFolderId);
    }
}
